import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, Chip, CircularProgress, Button } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { CarrierData, Shipment, UnbilledShipmentsFilters, UnbilledShipmentsResponse } from '../types';
import { fetchUnbilledPurchaseShipments, getCarriers } from '../../../pages/PurchaseInvoice/api/api';
import DateRangePicker from '@Components/DateRangePicker';
import { initialDateRange } from '../../../pages/PurchaseInvoice/constants';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { getSystemList } from '@Reducers/Systems';
import moment from 'moment';
import SbButton from '@Components/Button';
import FilterSelect from '@Components/Filter/Dropdown';
import { formatLabel } from './constant';

interface Carrier {
  id: string;
  name: string;
  image?: string;
  code: string;
  status: 'ready' | 'pending' | 'partial';
  totalShipments: number;
  totalInvoiceAmount: number;
  currency: string;
}

interface CarrierOptions {
  value: string;
  display: string;
}

interface Props {
  selectedCarrier: Carrier | null;
  setSelectedCarrier: (carrier: Carrier) => void;
  setServiceProviderData: React.Dispatch<React.SetStateAction<Shipment[]>>;
}

const Step1SelectCarrier: React.FC<Props> = ({ selectedCarrier, setSelectedCarrier, setServiceProviderData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [carriersData, setCarriersData] = useState<Record<string, CarrierData>>({});
  const [carrierOptions, setCarrierOptions] = useState<CarrierOptions[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<UnbilledShipmentsFilters>({});
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState({ ...initialDateRange });
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);

  const SystemList = useSelector((state: RootStateType) => state.System.data.selectedList).map(system => ({
    display: system.name,
    value: system._id,
  }));

  const activeSystemList = useMemo(() => {
    if (_systems?.length) return SystemList.filter(system => _systems.includes(system.value));
    else return [];
  }, [SystemList, _systems]);

  // Fetch carriers for dropdown
  useEffect(() => {
    dispatch(getSystemList());
    const fetchData = async () => {
      try {
        const data = await getCarriers();
        const mappedCarriersOptions = data?.map((c: { _id: string; name: string; code: string }) => ({
          value: c._id,
          display: c.name,
        }));
        setCarrierOptions(mappedCarriersOptions);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  // Load carriers from API
  const loadCarriers = async () => {
    try {
      setLoading(true);

      const carriersToSend: string[] =
        filters.carriers && filters.carriers.length > 0 ? filters.carriers : carrierOptions.map(c => c.value);

      const response: UnbilledShipmentsResponse = await fetchUnbilledPurchaseShipments({
        ...filters,
        carriers: carriersToSend,
        fromDate: moment(dateRange.startDate).format('DD/MM/YYYY'),
        toDate: moment(dateRange.endDate).format('DD/MM/YYYY'),
      });

      setCarriersData(response.data);
    } catch (err) {
      console.error('Error fetching carriers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (carrierOptions.length === 0) return;
    loadCarriers();
  }, [filters, dateRange, carrierOptions]);

  // Date change handler
  const onDateChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
    if (isOpen) return;
    setDateRange({ startDate, endDate });
  };

  // System filter change
  const handleSystemChange = (event: any) => {
    setFilters({
      ...filters,
      systems: typeof event === 'string' ? event.split(',') : event,
    });
  };

  // Carrier filter change
  const handleCarrierChange = (event: any) => {
    setFilters({
      ...filters,
      carriers: typeof event === 'string' ? event.split(',') : event,
    });
  };

  // Transform carriersData to array for frontend display
  const carriersArray: Carrier[] = useMemo(() => {
    return Object.values(carriersData).map(c => ({
      id: c.carrierId,
      name: c.code,
      code: c.code,
      status: 'ready',
      totalShipments: c.shipmentCount,
      totalInvoiceAmount: c.amount,
      currency: c.currency,
    }));
  }, [carriersData]);

  // Apply search filter
  const filteredCarriers = !search
    ? carriersArray
    : carriersArray.filter(
        c =>
          (c.name?.toLowerCase() ?? '').includes(search.toLowerCase()) ||
          (c.code?.toLowerCase() ?? '').includes(search.toLowerCase()),
      );

  const handleClearFilters = () => {
    setFilters({});
    setDateRange({ ...initialDateRange });
  };

  return (
    <Box>
      {/* Filters Section */}
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <TextField
            placeholder="Search carriers..."
            variant="outlined"
            size="small"
            sx={{ flex: 1, minWidth: '400px' }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <DateRangePicker onChange={onDateChange} value={dateRange} />
        </Box>

        <Box display={'flex'} alignItems={'center'} gap={2}>
          <FilterSelect
            id="system"
            label="System"
            value={filters.systems || []}
            options={activeSystemList}
            onChange={handleSystemChange}
          />
          <FilterSelect
            id="carriers"
            label="Carriers"
            value={filters.carriers || []}
            options={carrierOptions}
            onChange={handleCarrierChange}
          />
          <SbButton
            className="xs-text ml-1"
            variant="outlined"
            sx={{ borderRadius: '8px !important', padding: '8px !important', whiteSpace: 'nowrap', minWidth: '100px' }}
            onClick={handleClearFilters}
          >
            Clear Filters
          </SbButton>
        </Box>
      </Box>

      <Box my={'2rem'} pb={'0.5rem'} borderBottom={'4px solid #080808'} width={'220px'}>
        <Typography className="md-text" fontWeight={700}>
          Select Unbilled Carriers
        </Typography>
      </Box>

      {/* Carrier List */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredCarriers.map(carrier => (
            <Box key={carrier.id}>
              <Box
                display="grid"
                gridTemplateColumns="1fr auto 1fr"
                alignItems="center"
                border={'1px solid #e0e0e0'}
                margin={'10px 0'}
                borderRadius={3}
                p={2}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' } }}
                onClick={() => {
                  setSelectedCarrier(carrier);
                  const carrierDetail = carriersData[carrier.id];
                  if (carrierDetail) {
                    setServiceProviderData(carrierDetail.shipments);
                  }
                }}
              >
                {/* Left Side - Carrier Info with Image/Initials */}
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                    }}
                  >
                    {/* Show image if available, otherwise show initials */}
                    {carrier?.image ? (
                      <img
                        src={carrier?.image}
                        alt={carrier.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      carrier.name?.charAt(0)?.toUpperCase() || 'C'
                    )}
                  </Box>
                  <Box>
                    <Typography fontWeight="bold">{formatLabel(carrier.name)?.toUpperCase()}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Code: {carrier.code}
                    </Typography>
                  </Box>
                </Box>

                {/* Center - Shipments Count */}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  sx={{
                    minWidth: 160, // keeps center alignment consistent
                    height: '100%', // ensures vertical centering relative to card
                  }}
                >
                  <Chip
                    icon={<LocalShippingIcon fontSize="small" />}
                    label={`${carrier.totalShipments} shipments`}
                    variant="outlined"
                    size="medium"
                    sx={{
                      fontWeight: 500,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      px: 1.5,
                      py: 0.5,
                    }}
                  />
                </Box>

                {/* Right Side - Amount and Select Button */}
                <Box display="flex" justifyContent={'flex-end'} alignItems="center" gap={3}>
                  <Typography variant="body1" fontWeight="600" sx={{ textAlign: 'center' }}>
                    {carrier.currency} {carrier.totalInvoiceAmount?.toLocaleString()}
                  </Typography>
                  <Button
                    variant={selectedCarrier?.id === carrier.id ? 'contained' : 'outlined'}
                    sx={{
                      borderRadius: 5,
                      px: 2.5,
                      py: 1,
                      fontSize: '14px',
                      minWidth: 100,
                      fontWeight: '600',
                    }}
                  >
                    {selectedCarrier?.id === carrier.id ? 'Selected' : 'Select'}
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Step1SelectCarrier;
